import type { ExpenseEntry } from './types';

/**
 * Que recepciones vienen marcadas al abrir el cuadro de conciliacion.
 *
 * Hasta el 9 sep 2026 se marcaban TODAS. Con dos o tres entregas es comodo
 * —confirmas y listo—, pero un proveedor con veinte albaranes pendientes de
 * tres meses obliga a desmarcar quince, y lo que de verdad pasa entonces es
 * que alguien confirma sin mirar y cierra entregas que esa factura no cubre.
 *
 * La regla, en una frase: se marcan las entregas MAS ANTIGUAS que caben en el
 * importe de la factura. Sale de como se factura de verdad —lo viejo primero—
 * y tiene dos atajos que importan:
 *
 *  - Si todo lo pendiente cuadra a grandes rasgos con la factura, se marca
 *    entero. Es el caso normal (la factura de fin de mes engloba el mes) y no
 *    se rompe porque un descuento haga que la factura sea unos euros menor que
 *    la suma de los albaranes: ir llenando dejaria el ultimo fuera y la
 *    diferencia seria enorme.
 *  - Si ni la mas antigua cabe, no se marca ninguna. Mejor que el usuario
 *    elija a que le proponemos algo que no cuadra.
 *
 * Nunca decide por nadie: son casillas y la diferencia se ve en pantalla.
 */

/** Cuanto puede pasarse la suma del importe de la factura y seguir siendo «todo». */
const MARGEN = 0.05;

type Candidato = Pick<ExpenseEntry, 'entryId' | 'date' | 'netAmount'>;

export function seleccionSugerida(
  candidatos: Candidato[],
  importeFactura: number,
): string[] {
  if (candidatos.length === 0) return [];

  // Sin importe no hay nada en lo que «caber»: no se marca ninguna. Devolver
  // todas aqui fue lo que convirtio una factura fuera del periodo en pantalla
  // en «cerrar veinte entregas de golpe» (revision del 9 sep 2026).
  if (importeFactura <= 0) return [];
  const total = candidatos.reduce((suma, c) => suma + c.netAmount, 0);
  // Cabe todo (o se pasa por un descuento de poca monta): se marca todo.
  if (total <= importeFactura * (1 + MARGEN)) {
    return candidatos.map((c) => c.entryId);
  }

  // Si no, de la mas antigua a la mas nueva mientras quepan.
  const porFecha = [...candidatos].sort((a, b) => a.date.localeCompare(b.date));
  const elegidas: string[] = [];
  // En centimos: con decimales, 28,10 + 35,95 + 35,95 «pasa» de 100,00.
  const tope = Math.round(importeFactura * 100);
  let acumulado = 0;
  for (const candidato of porFecha) {
    const cents = Math.round(candidato.netAmount * 100);
    if (acumulado + cents > tope) break;
    acumulado += cents;
    elegidas.push(candidato.entryId);
  }
  return elegidas;
}

/**
 * Fecha en la que salio el dinero de una factura que se da por pagada al
 * conciliar: el ultimo pago de sus notas, o el dia de la conciliacion si
 * ninguna lleva fecha. La usan la API y la demo, que antes la repetian.
 */
export function ultimaFechaDePago(
  notas: Array<Pick<ExpenseEntry, 'paymentDate'>>,
  siNinguna: string,
): string {
  return (
    notas
      .map((n) => n.paymentDate)
      .filter((d): d is string => Boolean(d))
      .sort()
      .at(-1) ?? siNinguna
  );
}
